// WhatsApp sharing with the PDF attached (share sheet on phone/tablet; download + chat on desktop).

// For records with no title field (appointments, inspections): keep the name.
export const greetOrName = (prefix, name) => (prefix ? greetName(prefix, name) : (String(name || '').trim() || 'Sir/Madam'));

const TITLED = /^(mr|mrs|miss|ms|dr|prof|m\/s)(\.|\s)/i;
// Older customers sometimes have the title typed into the name itself.
export const greetName = (prefix, name) => {
  if (prefix) return `${prefix} ${name || ''}`.trim();
  return TITLED.test(String(name || '').trim()) ? name.trim() : 'Sir/Madam';
};

// iPadOS often reports itself as a Mac; touch points give it away.
export const isIpad = () => typeof navigator !== 'undefined'
  && (/iPad/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1));

// iPads usually lack the WhatsApp app, so they go straight to WhatsApp Web.
export const waUrl = (phone, message) => {
  const digits = String(phone || '').replace(/\D/g, '');
  const num = digits.length === 10 ? `91${digits}` : digits;
  const text = encodeURIComponent(message);
  if (isIpad()) return `https://web.whatsapp.com/send?${num.length >= 10 ? `phone=${num}&` : ''}text=${text}`;
  // api.whatsapp.com directly: the wa.me redirect garbles emoji on desktop browsers.
  return `https://api.whatsapp.com/send?${num.length >= 10 ? `phone=${num}&` : ''}text=${text}`;
};

let signature = {};
export const setSignature = (s) => { signature = s || {}; };

// Closing lines for every customer message, from Billing Settings.
export const signOff = ({ noReview = false } = {}) => {
  const s = noReview ? { ...signature, review_url: '' } : signature;
  const out = ['', s.thank_you || 'Thank you for choosing us.', `— ${s.company_name || 'Team'}`];
  if (s.phone) out.push(`Call us: ${s.phone}`);
  if (s.review_url) out.push('', `Happy with our work? Please leave us a Google review: ${s.review_url}`);
  return out;
};

// Templates come from /tenant/me (workshop overrides merged over defaults).
let templates = {};
export const setTemplates = (t) => { templates = t || {}; };
const INLINE = new Set(['for_subject', 'gst_note', 'advance_status', 'total_note']);
const FIELD = /\{(\w+)\}/g;

// Same rules as backend message_templates.render: a line drops when a known field on it is empty.
export const renderTemplate = (text, values, opts) => {
  const out = [];
  String(text || '').split('\n').forEach((line) => {
    const keys = [...line.matchAll(FIELD)].map((m) => m[1]).filter((k) => k in values);
    if (keys.some((k) => !INLINE.has(k) && values[k] !== true && !values[k])) return;
    let l = line.replace(FIELD, (m, k) => (k in values ? (values[k] === true ? '' : String(values[k] ?? '')) : m));
    if (l.includes('{signature}')) l = l.replace('{signature}', signOff(opts).join('\n'));
    out.push(l);
  });
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

export const templatesFor = (key) => templates[key] || '';
export const renderMessage = (key, values) => renderTemplate(templates[key] || '', values);

export const signatureCompany = () => signature.company_name || '';

let showHost = null;
export const registerShareHost = (fn) => { showHost = fn; };

const download = (file) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(file);
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 1000);
};

// getPdf: async () => Blob. Fetches first, then hands a ready file to the share dialog.
export async function sharePdfToWhatsApp({ getPdf, filename, title, message, phone, type = 'application/pdf', kind = 'PDF' }) {
  let file = null;
  try {
    const blob = await getPdf();
    file = new File([blob], filename, { type });
  } catch (e) {
    console.warn('[whatsapp-share] PDF fetch failed, sending text only:', e);
  }
  const canShareFile = !!(file && navigator.canShare && navigator.canShare({ files: [file] }));
  if (file && !canShareFile) download(file);
  const payload = { file, title, message, kind, url: waUrl(phone, message), mode: canShareFile ? 'share' : 'desktop' };
  if (showHost) showHost(payload);
  else window.open(payload.url, '_blank', 'noopener');
}
